import type { FC } from 'react';
import React, { useCallback } from 'react';
import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import EmptyState from '@atlaskit/empty-state/empty-state';
import Button from '@atlaskit/button/button';

import GenericErrorImg from './GenericErrorImg.svg';

export const i18n: {
	errorHeader: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	buttonText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	errorHeader: {
		id: 'feed.error.generic.header',
		defaultMessage: 'We couldn’t load the feed',
		description:
			'error message header that is presented when a data or JS error stops us from rendering any useful information in the feed list',
	},
	errorDescription: {
		id: 'feed.error.generic.description',
		defaultMessage: 'Refresh to try again.',
		description:
			'error message description that is presented when a data or JS error stops us from rendering any useful information in the feed list',
	},
	buttonText: {
		id: 'feed.error.generic.button.text',
		defaultMessage: 'Refresh',
		description: 'action on error message that allows the user to refresh their browser',
	},
});

const GenericFeedErrorComponent: FC<WrappedComponentProps> = ({ intl }) => {
	const handleRefreshPage = useCallback(() => {
		window.location.reload();
	}, []);

	const refreshButton = (
		<Button onClick={handleRefreshPage}>
			<FormattedMessage {...i18n.buttonText} />
		</Button>
	);

	return (
		<EmptyState
			header={intl.formatMessage(i18n.errorHeader)}
			description={intl.formatMessage(i18n.errorDescription)}
			imageUrl={GenericErrorImg}
			imageHeight={95}
			primaryAction={refreshButton}
		/>
	);
};

export const GenericFeedError: FC<WithIntlProps<WrappedComponentProps>> & {
	WrappedComponent: React.ComponentType<WrappedComponentProps>;
} = injectIntl(GenericFeedErrorComponent);
